/**
 * 연장근로 요청 Mutations Hook
 *
 * V176: Cloud Function callable 방식으로 전환 (서버 권위)
 * - 기존: Firestore addDoc 직접 호출 → 권한 에러 발생
 * - 변경: createOvertimeRequest Cloud Function 호출
 *
 * **서버 권위:**
 * - employeeId: 서버에서 auth.uid로 확정 (클라이언트 입력 무시)
 * - employeeName: 서버에서 users/{uid} 조회 (fallback 체인)
 * - approverId: 서버에서 SSoT 규칙으로 결정
 * - 트랜잭션: overtimeRequests + approvalRequests 동시 생성
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { OvertimeRequestInput } from '../types/overtime';
import { useCurrentUser } from './useCurrentUser';

/**
 * createOvertimeRequest Cloud Function 응답 타입
 */
interface CreateOvertimeResponse {
  success: boolean;
  overtimeRequestId: string;
  approvalRequestId: string;
  approverId: string;
  approverName: string;
}

export function useOvertimeMutations() {
  const { user } = useCurrentUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 연장근로 요청 제출 (V176: Cloud Function 방식)
   *
   * **V175 → V176 변경점:**
   * - 기존: Firestore addDoc 직접 호출 → Firestore Rules 권한 에러
   * - 변경: createOvertimeRequest Cloud Function callable 호출
   *
   * **서버 권위:**
   * - employeeId/employeeName: 서버에서 확정 (클라이언트 입력 무시)
   * - approverId/approverName: 서버에서 SSoT 규칙으로 결정
   * - 트랜잭션: overtimeRequests + approvalRequests 동시 생성 (원자성 보장)
   *
   * **검증 (서버):**
   * - endDateOffset=0: endTime > startTime 필수
   * - endDateOffset=1: 익일 종료 (+1440분 계산)
   * - 24시간 초과 시 거부
   *
   * @param input 연장근로 요청 입력
   * @returns overtimeRequestId
   */
  const submitOvertimeRequest = async (input: OvertimeRequestInput): Promise<string> => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // V176: Cloud Function callable 호출
      // V121: projectId 필수 추가
      const functions = getFunctions();
      const createOvertimeRequestFn = httpsCallable<
        {
          date: string;
          type: 'overtime' | 'weekend' | 'holiday';
          startTime: string;
          endTime: string;
          endDateOffset?: 0 | 1;
          reason?: string;
          projectId: string;  // V121: 프로젝트 ID 필수
        },
        CreateOvertimeResponse
      >(functions, 'createOvertimeRequest');

      const result = await createOvertimeRequestFn({
        date: input.date,
        type: input.type,
        startTime: input.startTime,
        endTime: input.endTime,
        endDateOffset: input.endDateOffset ?? 0,
        reason: input.reason || '',  // undefined 방어
        projectId: input.projectId,  // V121: 프로젝트 ID
      });

      const { overtimeRequestId, approverId, approverName } = result.data;

      return overtimeRequestId;
    } catch (err: any) {
      // Firebase Functions 에러 메시지 추출
      let errorMessage = '연장근로 요청 제출 중 오류가 발생했습니다.';
      if (err.code === 'functions/unauthenticated') {
        errorMessage = '로그인이 필요합니다.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || '입력 값이 올바르지 않습니다.';
      } else if (err.code === 'functions/failed-precondition') {
        // no-approver 에러 등
        errorMessage = err.message || '요청 조건이 충족되지 않았습니다.';
      } else if (err.code === 'functions/not-found') {
        errorMessage = err.message || '필요한 정보를 찾을 수 없습니다.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      const finalError = new Error(errorMessage);
      setError(finalError);
      throw finalError;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitOvertimeRequest,
    isSubmitting,
    error,
  };
}
